import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, Trash2, Bug } from "lucide-react";
import { forceReviewDataRefresh } from "@/utils/cacheUtils";
import { toast } from "sonner";
import { debugLogger } from '@/utils/debug';

interface CacheDebugPanelProps {
  providerId?: string;
}

export const CacheDebugPanel = ({ providerId }: CacheDebugPanelProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const queryClient = useQueryClient();

  const handleClearAllCaches = async () => {
    setIsProcessing(true);
    try {
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();
      toast.success("All caches cleared successfully");
      debugLogger.log("All caches and storage cleared");
    } catch (error) {
      console.error("Error clearing caches:", error);
      toast.error("Failed to clear caches");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleForceRefreshProvider = async () => {
    if (!providerId) {
      toast.error("No provider ID provided");
      return;
    }

    setIsProcessing(true);
    try {
      await forceReviewDataRefresh(queryClient, providerId);
      toast.success(`Force refreshed data for provider ${providerId}`);
    } catch (error) {
      console.error("Error force refreshing provider data:", error);
      toast.error("Failed to force refresh provider data");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShowCacheState = () => {
    const queries = queryClient.getQueryCache().getAll();
    const serviceReviewQueries = queries.filter(q => q.queryKey[0] === 'serviceReviews');
    
    debugLogger.log("=== CACHE DEBUG INFO ===");
    debugLogger.log("All queries in cache:", queries.length);
    debugLogger.log("ServiceReview queries:", serviceReviewQueries.length);
    serviceReviewQueries.forEach(query => {
      debugLogger.log("ServiceReview query:", {
        key: query.queryKey,
        status: query.state.status,
        dataUpdatedAt: new Date(query.state.dataUpdatedAt).toLocaleString(),
        data: query.state.data ? `${Array.isArray(query.state.data) ? query.state.data.length : 'Unknown'} reviews` : 'No data'
      });
    });
    debugLogger.log("localStorage keys:", Object.keys(localStorage));
    debugLogger.log("sessionStorage keys:", Object.keys(sessionStorage));
    debugLogger.log("=== END CACHE DEBUG ===");
    
    toast.success("Cache state logged to console");
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bug className="h-5 w-5" />
          Cache Debug Panel
        </CardTitle>
        <CardDescription>
          Debug tools for troubleshooting cache invalidation issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            onClick={handleShowCacheState}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Bug className="h-4 w-4" />
            Log Cache State
          </Button>
          
          <Button
            onClick={handleClearAllCaches}
            variant="destructive"
            size="sm"
            disabled={isProcessing}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Clear All Caches
          </Button>
          
          {providerId && (
            <Button
              onClick={handleForceRefreshProvider}
              variant="secondary"
              size="sm"
              disabled={isProcessing}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Force Refresh Provider
            </Button>
          )}
        </div>
        
        {providerId && (
          <div className="text-xs text-muted-foreground">
            Provider ID: {providerId}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
